import { ImportButton } from "@/components/ImportButton";

import React, { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, fileUrl, downloadXlsx } from "@/lib/api";
import { fmtEuro, fmtDate, EXP_STATUS_LABEL } from "@/lib/format";
import { companyMeta } from "@/lib/company";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Plus, Upload, Check, X, ReceiptText, Image as ImageIcon, Camera, ScanLine, Save, Pencil, Filter, FileSpreadsheet, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const STATUS = {
  pending: "bg-[#FFFBEB] text-[#92400E]",
  approved: "bg-[#ECFDF5] text-[#065F46]",
  rejected: "bg-[#FEF2F2] text-[#991B1B]",
};
const inp = "mt-1 w-full rounded-lg border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";
const lab = "text-xs uppercase tracking-wider text-[#6F6A60] font-semibold";
const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ category: companyMeta().expenseCategories[0], amount: "", description: "", date: today(),
                       supplier: "", net_amount: "", vat_amount: "", invoice_date: "" });

function SubmitDialog() {
  const qc = useQueryClient();
  const fileRef = useRef();
  const cameraRef = useRef();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [fileId, setFileId] = useState(null);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [dups, setDups] = useState([]);

  const checkDups = async (amount, date) => {
    if (!amount || !date) return;
    try {
      const { data } = await api.get(`/expenses/duplicates?amount=${amount}&date=${date}`);
      setDups(data.duplicates || []);
    } catch {
      setDups([]);
    }
  };

  const runOcr = async (id) => {
    setScanning(true);
    try {
      const { data } = await api.post("/expenses/ocr", { file_id: id });
      const f = data.fields || {};
      setForm((p) => ({
        ...p,
        supplier: f.supplier || p.supplier,
        amount: f.total ? String(f.total) : p.amount,
        net_amount: f.net ? String(f.net) : p.net_amount,
        vat_amount: f.vat ? String(f.vat) : p.vat_amount,
        invoice_date: f.date || p.invoice_date,
        date: f.date || p.date,
      }));
      setScanned(true);
      toast.success("Τα στοιχεία διαβάστηκαν — έλεγξέ τα και αποθήκευσε");
      await checkDups(f.total, f.date);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setScanning(false);
    }
  };

  const uploadFile = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileId(data.file_id);
      setFileName(f.name);
      toast.success("Η απόδειξη ανέβηκε — διαβάζω τα στοιχεία…");
      await runOcr(data.file_id);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setUploading(false);
    }
  };

  const submit = useMutation({
    mutationFn: async () => (await api.post("/expenses", {
      ...form,
      amount: Number(form.amount) || 0,
      net_amount: Number(form.net_amount) || 0,
      vat_amount: Number(form.vat_amount) || 0,
      file_id: fileId,
      ocr_status: scanned ? "ocr" : "",
    })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      toast.success(d.email_sent ? "Το έξοδο υποβλήθηκε & στάλθηκε στο λογιστήριο" : "Το έξοδο υποβλήθηκε");
      setOpen(false);
      setForm(blank());
      setFileId(null); setFileName(""); setScanned(false);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="submit-expense-btn" className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A]">
          <Plus className="h-4 w-4" /> Υποβολή Εξόδου
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-xl">Υποβολή Εξόδου</DialogTitle></DialogHeader>
        <div>
          <label className={lab}>Φωτογραφία / PDF παραστατικού</label>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadFile(e.target.files[0])} data-testid="exp-file-input" />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadFile(e.target.files[0])} data-testid="exp-camera-input" />
          <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button type="button" data-testid="exp-camera-btn" onClick={() => cameraRef.current?.click()} disabled={uploading || scanning}
              className="rounded-lg bg-[#45423C] px-3 py-4 text-sm font-semibold text-white flex items-center justify-center gap-2 hover:bg-[#56524A] disabled:opacity-50">
              <Camera className="h-4 w-4" /> {uploading ? "Ανέβασμα…" : "Φωτογραφία με κάμερα"}
            </button>
            <button type="button" data-testid="exp-file-btn" onClick={() => fileRef.current?.click()} disabled={uploading || scanning}
              className="rounded-lg border border-dashed border-[#B9AE9A] bg-[#B9AE9A]/5 px-3 py-4 text-sm text-[#6F6A60] flex items-center justify-center gap-2 hover:bg-[#B9AE9A]/10 disabled:opacity-50">
              <Upload className="h-4 w-4" /> {fileName || "Επιλογή αρχείου (φωτό/PDF)"}
            </button>
          </div>
          <div className="mt-2 flex items-center gap-2 text-[11px]" data-testid="ocr-state">
            {scanning ? (
              <span className="inline-flex items-center gap-1.5 text-[#C86235] font-semibold"><ScanLine className="h-3.5 w-3.5 animate-pulse" /> Ανάγνωση παραστατικού με AI…</span>
            ) : scanned ? (
              <span className="inline-flex items-center gap-1.5 text-[#065F46] font-semibold"><Check className="h-3.5 w-3.5" /> Τα πεδία συμπληρώθηκαν αυτόματα — διόρθωσέ τα αν χρειάζεται</span>
            ) : (
              <span className="text-[#6F6A60]">Μετά το ανέβασμα διαβάζονται αυτόματα προμηθευτής, ημερομηνία, καθαρή αξία, ΦΠΑ και σύνολο.</span>
            )}
            {fileId && !scanning && (
              <button type="button" data-testid="ocr-retry" onClick={() => runOcr(fileId)} className="text-[#C86235] font-semibold hover:underline">
                Επανάληψη
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-2">
          {dups.length > 0 && (
            <div className="col-span-2 rounded-xl border border-[#C86235]/40 bg-[#FEF7F4] p-3" data-testid="duplicate-warning">
              <div className="flex items-center gap-2 text-[#a24a24] text-sm font-semibold">
                <AlertTriangle className="h-4 w-4" /> Πιθανή διπλή καταχώρηση ({dups.length})
              </div>
              <ul className="mt-1 space-y-0.5 text-[11px] text-[#6F6A60]">
                {dups.map((x) => (
                  <li key={x.id} data-testid={`dup-${x.id}`}>
                    {x.employee_name} · {x.supplier || x.category} · {fmtDate(x.invoice_date || x.date)} · {fmtEuro(x.amount)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="col-span-2">
            <label className={lab}>Προμηθευτής</label>
            <input data-testid="exp-supplier" className={inp} value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="Επωνυμία εκδότη" />
          </div>
          <div>
            <label className={lab}>Ημερομηνία παραστατικού</label>
            <input data-testid="exp-invoice-date" type="date" className={inp} value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
          </div>
          <div>
            <label className={lab}>Ημερομηνία εξόδου</label>
            <input data-testid="exp-date" type="date" className={inp} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className={lab}>Καθαρή αξία (€)</label>
            <input data-testid="exp-net" type="number" step="0.01" className={inp} value={form.net_amount} onChange={(e) => setForm({ ...form, net_amount: e.target.value })} />
          </div>
          <div>
            <label className={lab}>ΦΠΑ (€)</label>
            <input data-testid="exp-vat" type="number" step="0.01" className={inp} value={form.vat_amount} onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} />
          </div>
          <div>
            <label className={lab}>Συνολικό ποσό (€)</label>
            <input data-testid="exp-amount" type="number" step="0.01" className={inp} value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              onBlur={(e) => checkDups(e.target.value, form.invoice_date || form.date)} />
          </div>
          <div>
            <label className={lab}>Κατηγορία</label>
            <select data-testid="exp-category" className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {companyMeta().expenseCategories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className={lab}>Περιγραφή</label>
            <input data-testid="exp-desc" className={inp} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Για ποιον σκοπό;" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="exp-submit" onClick={() => submit.mutate()} disabled={submit.isPending || !form.amount}
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
            <Save className="h-4 w-4" /> {submit.isPending ? "Υποβολή…" : "Αποθήκευση"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ exp, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    supplier: exp.supplier || "", category: exp.category, amount: String(exp.amount ?? ""),
    net_amount: String(exp.net_amount ?? ""), vat_amount: String(exp.vat_amount ?? ""),
    invoice_date: exp.invoice_date || "", date: exp.date || today(), description: exp.description || "",
  });
  const save = useMutation({
    mutationFn: async () => (await api.put(`/expenses/${exp.id}`, {
      ...form, amount: Number(form.amount) || 0,
      net_amount: Number(form.net_amount) || 0, vat_amount: Number(form.vat_amount) || 0,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["expenses"] }); toast.success("Αποθηκεύτηκε"); onClose(); },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="exp-edit-dialog">
        <DialogHeader><DialogTitle className="font-serif text-xl">Επεξεργασία εξόδου</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className={lab}>Προμηθευτής</label>
            <input data-testid="edit-supplier" className={inp} value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
          </div>
          <div>
            <label className={lab}>Ημ/νία παραστατικού</label>
            <input data-testid="edit-invoice-date" type="date" className={inp} value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
          </div>
          <div>
            <label className={lab}>Ημ/νία εξόδου</label>
            <input data-testid="edit-date" type="date" className={inp} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className={lab}>Καθαρή αξία (€)</label>
            <input data-testid="edit-net" type="number" step="0.01" className={inp} value={form.net_amount} onChange={(e) => setForm({ ...form, net_amount: e.target.value })} />
          </div>
          <div>
            <label className={lab}>ΦΠΑ (€)</label>
            <input data-testid="edit-vat" type="number" step="0.01" className={inp} value={form.vat_amount} onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} />
          </div>
          <div>
            <label className={lab}>Σύνολο (€)</label>
            <input data-testid="edit-amount" type="number" step="0.01" className={inp} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div>
            <label className={lab}>Κατηγορία</label>
            <select data-testid="edit-category" className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {companyMeta().expenseCategories.map((c) => <option key={c}>{c}</option>)}
              {!companyMeta().expenseCategories.includes(form.category) && <option>{form.category}</option>}
            </select>
          </div>
          <div className="col-span-2">
            <label className={lab}>Περιγραφή</label>
            <input data-testid="edit-desc" className={inp} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="edit-save" onClick={() => save.mutate()} disabled={save.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
            <Save className="h-4 w-4" /> Αποθήκευση
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Expenses() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ status: "all", category: "all", employee: "all", date_from: "", date_to: "", q: "" });
  const canReview = ["owner", "hr", "manager"].includes(user?.role);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [f]);

  const { data, isLoading } = useQuery({
    queryKey: ["expenses", params],
    queryFn: async () => (await api.get(`/expenses?${params}`)).data,
  });
  const rows = data?.rows || [];

  const review = useMutation({
    mutationFn: async ({ id, action }) => (await api.put(`/expenses/${id}/${action}`, { note: "" })).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["expenses"] }); toast.success(`Το έξοδο: ${EXP_STATUS_LABEL[d.status]}`); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const sel = "rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]";

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex justify-end"><ImportButton only={["expenses"]} label="Ανέβασμα εξόδων (Excel)" /></div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Αποζημιώσεις</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Έξοδα</h1>
          <p className="text-[#6F6A60] text-sm mt-1">
            Αρχείο παραστατικών για ενημέρωση και έγκριση — δεν ενημερώνει P&amp;L ή προϋπολογισμό.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-expenses-xlsx"
            onClick={() => downloadXlsx(`/expenses/export.xlsx?${params}`, "TAF_Exoda.xlsx")}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#45423C] hover:border-[#B9AE9A]">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </button>
          <SubmitDialog />
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-4" data-testid="expense-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-[#C86235]" />
          <h2 className="text-sm font-semibold text-[#45423C]">Φίλτρα</h2>
          <span className="ml-auto text-xs text-[#6F6A60] tabular" data-testid="expense-total">
            {data?.count || 0} έξοδα · σύνολο {fmtEuro(data?.total || 0)}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <select data-testid="filter-status" className={sel} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            <option value="all">Όλες οι καταστάσεις</option>
            <option value="pending">Εκκρεμεί</option>
            <option value="approved">Εγκρίθηκε</option>
            <option value="rejected">Απορρίφθηκε</option>
          </select>
          <select data-testid="filter-category" className={sel} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
            <option value="all">Όλες οι κατηγορίες</option>
            {(data?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {canReview && (
            <select data-testid="filter-employee" className={sel} value={f.employee} onChange={(e) => setF({ ...f, employee: e.target.value })}>
              <option value="all">Όλοι οι χρήστες</option>
              {(data?.employees || []).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          )}
          <input data-testid="filter-from" type="date" className={sel} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
          <input data-testid="filter-to" type="date" className={sel} value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
          <input data-testid="filter-q" className={sel} placeholder="Αναζήτηση…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                {canReview && <th className="px-4 py-3">Υπάλληλος</th>}
                <th className="px-4 py-3">Προμηθευτής</th>
                <th className="px-4 py-3">Κατηγορία</th>
                <th className="px-4 py-3">Ημ/νία</th>
                <th className="px-4 py-3 text-right">Καθαρή</th>
                <th className="px-4 py-3 text-right">ΦΠΑ</th>
                <th className="px-4 py-3 text-right">Σύνολο</th>
                <th className="px-4 py-3">Απόδειξη</th>
                <th className="px-4 py-3">Κατάσταση</th>
                <th className="px-4 py-3 text-right">Ενέργεια</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {!isLoading && rows.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-[#6F6A60]">Δεν βρέθηκαν έξοδα με αυτά τα φίλτρα.</td></tr>}
              {rows.map((x) => (
                <tr key={x.id} data-testid={`expense-row-${x.id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  {canReview && <td className="px-4 py-3 font-medium text-[#45423C]">{x.employee_name}</td>}
                  <td className="px-4 py-3 text-[#45423C]">{x.supplier || "—"}</td>
                  <td className="px-4 py-3">{x.category}</td>
                  <td className="px-4 py-3 text-[#6F6A60]">{fmtDate(x.invoice_date || x.date)}</td>
                  <td className="px-4 py-3 text-right tabular text-[#6F6A60]">{x.net_amount ? fmtEuro(x.net_amount) : "—"}</td>
                  <td className="px-4 py-3 text-right tabular text-[#6F6A60]">{x.vat_amount ? fmtEuro(x.vat_amount) : "—"}</td>
                  <td className="px-4 py-3 text-right tabular font-medium">{fmtEuro(x.amount)}</td>
                  <td className="px-4 py-3">
                    {x.file_id ? (
                      <button data-testid={`view-receipt-${x.id}`} onClick={() => setPreview(x.file_id)} className="inline-flex items-center gap-1 text-[#C86235] text-xs font-medium">
                        <ImageIcon className="h-4 w-4" /> Προβολή
                      </button>
                    ) : <span className="text-[#6F6A60] text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${STATUS[x.status]}`}>{EXP_STATUS_LABEL[x.status] || x.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2 items-center">
                      <button data-testid={`edit-${x.id}`} onClick={() => setEditing(x)} title="Επεξεργασία"
                        className="p-1.5 rounded-lg bg-[#F1EDE6] text-[#6F6A60] hover:text-[#45423C]">
                        <Pencil className="h-4 w-4" />
                      </button>
                      {canReview && x.status === "pending" && (
                        <>
                          <button data-testid={`approve-${x.id}`} onClick={() => review.mutate({ id: x.id, action: "approve" })} className="p-1.5 rounded-lg bg-[#ECFDF5] text-[#065F46] hover:opacity-80">
                            <Check className="h-4 w-4" />
                          </button>
                          <button data-testid={`reject-${x.id}`} onClick={() => review.mutate({ id: x.id, action: "reject" })} className="p-1.5 rounded-lg bg-[#FEF2F2] text-[#991B1B] hover:opacity-80">
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {canReview && x.status !== "pending" && <span className="text-xs text-[#6F6A60]">{x.reviewed_by || "—"}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <EditDialog exp={editing} onClose={() => setEditing(null)} />}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-lg flex items-center gap-2"><ReceiptText className="h-5 w-5 text-[#C86235]" /> Απόδειξη</DialogTitle></DialogHeader>
          {preview && (
            <>
              <object data={fileUrl(preview)} className="w-full h-[65vh] rounded-xl border border-[#E3DED4]" data-testid="receipt-image">
                <div className="p-6 text-center text-sm text-[#6F6A60]">Η προεπισκόπηση δεν είναι διαθέσιμη για αυτό το αρχείο.</div>
              </object>
              <a href={fileUrl(preview)} target="_blank" rel="noreferrer" data-testid="receipt-open-link"
                className="text-xs font-semibold text-[#C86235] hover:underline">Άνοιγμα σε νέα καρτέλα / λήψη →</a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
